import { ArrowRightOutlined, CheckOutlined, MailOutlined } from '@ant-design/icons';
import { useEffect } from 'react';
import { Link, useLocation } from "react-router-dom";

import { TextService } from '../../Services/TextService';

// Shared building blocks for the modernized marketing pages (Start2 and its sub pages).
// All styling lives in Start2.css, scoped under the .start2 root class.


// The app has no global scroll restoration, so in-app links would otherwise land mid-page.
export const useScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
};

export const integrations = [
  { name: "Swish", img: require('../../Images/swish.png'), href: "https://www.swish.com" },
  { name: "Fortnox", img: require('../../Images/fortnox.png'), href: "/fortnox-integration" },
  { name: "Bankgirot", img: require('../../Images/bankgirot.png'), href: "https://www.bankgirot.com" },
  { name: "Ping Payments", img: require('../../Images/pingpayments.png'), href: "/pingpayment-integration-partner" },
  { name: "Spiris", img: require('../../Images/spiris.png'), href: "/spiris-integration" },
];

export const CtaButtons = () => {
  const GetText = TextService.GetText;
  return <div className="s2-cta-row">
    <Link to="/se/CreateUser" className="s2-btn s2-btn-primary">{GetText("Start_Register")} <ArrowRightOutlined /></Link>
    <Link to="/se/pricing" className="s2-btn s2-btn-ghost">Se priser</Link>
  </div>
};

export const Crumbs = (props: { current: string, parent?: { to: string, text: string } }) =>
  <nav className="s2-crumbs" aria-label="Brödsmulor">
    <Link to="/se">Start</Link>
    {props.parent && <><span>/</span><Link to={props.parent.to}>{props.parent.text}</Link></>}
    <span>/</span>
    <span aria-current="page">{props.current}</span>
  </nav>;

export const Hero = (props: { crumb?: JSX.Element, eyebrow?: string, title: React.ReactNode, lead: React.ReactNode, image: string, imageAlt: string }) =>
  <section className="s2-hero">
    <div className="s2-container s2-hero-grid">
      <div className="s2-hero-text">
        {props.crumb}
        {props.eyebrow && <span className="s2-eyebrow">{props.eyebrow}</span>}
        <h1>{props.title}</h1>
        <p className="s2-lead">{props.lead}</p>
      </div>
      <div className="s2-hero-visual">
        <img alt={props.imageAlt} src={props.image} />
      </div>
    </div>
  </section>;

export const Checks = (props: { items: React.ReactNode[] }) =>
  <ul className="s2-checks">
    {props.items.map((item, i) => <li key={i}><CheckOutlined /> <span>{item}</span></li>)}
  </ul>;

export const SectionHead = (props: { title: React.ReactNode, text?: React.ReactNode }) =>
  <div className="s2-section-head">
    <h2>{props.title}</h2>
    {props.text && <p>{props.text}</p>}
  </div>;

export const Split = (props: { eyebrow?: string, title: React.ReactNode, children: React.ReactNode, visual: React.ReactNode, reverse?: boolean }) =>
  <div className={"s2-split" + (props.reverse ? " s2-split-reverse" : "")}>
    <div className="s2-split-text">
      {props.eyebrow && <span className="s2-eyebrow">{props.eyebrow}</span>}
      <h2>{props.title}</h2>
      {props.children}
    </div>
    <div className="s2-split-visual">{props.visual}</div>
  </div>;

// Framed screenshot of the real product UI.
export const Shot = (props: { src: string, alt: string }) =>
  <div className="s2-mock s2-shot">
    <div className="s2-mock-bar"><i /><i /><i /></div>
    <img src={props.src} alt={props.alt} />
  </div>;

export const IntegrationsStrip = (props: { title?: string }) =>
  <section className="s2-integrations-strip">
    <div className="s2-container">
      <p className="s2-logos-title">{props.title ?? "Automatisera dina flöden och koppla ihop OneFront med ledande betaltjänster"}</p>
      <div className="s2-integrations">
        {integrations.map(i => (
          <a key={i.name} href={i.href} target="_blank" rel="noopener noreferrer" title={i.name}>
            <img alt={i.name} src={i.img} />
          </a>
        ))}
      </div>
    </div>
  </section>;

export const CtaBanner = (props: { title?: string, text?: React.ReactNode }) => {
  const GetText = TextService.GetText;
  return <section className="s2-section s2-section-tight">
    <div className="s2-container">
      <div className="s2-cta-banner">
        <div>
          <h2>{props.title ?? "Kom igång på några minuter"}</h2>
          <p>{props.text ?? <>Skapa ett konto gratis, eller mejla <a href="mailto:support@onefront360.com">support@onefront360.com</a> så hjälper vi er personligen att komma igång.</>}</p>
        </div>
        <Link to="/se/CreateUser" className="s2-btn s2-btn-primary">{GetText("Start_Register")} <ArrowRightOutlined /></Link>
      </div>
    </div>
  </section>
};

export type FeatureItem = { icon: JSX.Element, title: string, html: string };

// Texts may contain <br/> from the text files, hence dangerouslySetInnerHTML (same as the old pages).
export const FeatureGrid = (props: { items: FeatureItem[] }) =>
  <div className="s2-feature-grid">
    {props.items.map(f => (
      <div className="s2-feature" key={f.title}>
        <span className="s2-feature-icon">{f.icon}</span>
        <div>
          <h4>{f.title}</h4>
          <p dangerouslySetInnerHTML={{ __html: f.html }} />
        </div>
      </div>
    ))}
  </div>;

export const Card = (props: { icon?: JSX.Element, title: React.ReactNode, children: React.ReactNode, tint?: "blue" | "orange" | "green" }) =>
  <div className={"s2-card" + (props.tint ? " s2-tint-" + props.tint : "")}>
    {props.icon && <span className="s2-pillar-icon">{props.icon}</span>}
    <h3>{props.title}</h3>
    {props.children}
  </div>;

// Stylized product mock-ups (HTML, not screenshots).
export const MockMembers = () =>
  <div className="s2-mock">
    <div className="s2-mock-bar"><i /><i /><i /></div>
    <div className="s2-mock-title">Medlemmar <span className="s2-pill">248 aktiva</span></div>
    {[["Anna Lindqvist", "Betald", "ok"], ["Erik Johansson", "Betald", "ok"], ["Sara Nilsson", "Påminnelse skickad", "warn"], ["Johan Berg", "Betald", "ok"]].map(([name, status, cls]) => (
      <div className="s2-mock-row" key={name}>
        <span className="s2-avatar">{name.charAt(0)}</span>
        <span className="s2-mock-name">{name}</span>
        <span className={`s2-status s2-status-${cls}`}>{status}</span>
      </div>
    ))}
  </div>;

export const MockEmail = () =>
  <div className="s2-mock">
    <div className="s2-mock-bar"><i /><i /><i /></div>
    <div className="s2-mock-title">Nytt utskick</div>
    <div className="s2-mock-field"><span>Till</span>Alla aktiva medlemmar (248)</div>
    <div className="s2-mock-field"><span>Ämne</span>Välkommen till årsmötet 12 mars</div>
    <div className="s2-mock-lines"><i /><i /><i style={{ width: "60%" }} /></div>
    <div className="s2-mock-actions"><span className="s2-mock-send"><MailOutlined /> Skicka</span></div>
  </div>;

export const MockPayments = () =>
  <div className="s2-mock">
    <div className="s2-mock-bar"><i /><i /><i /></div>
    <div className="s2-mock-title">Medlemsavgifter 2026</div>
    <div className="s2-stat-row">
      <div className="s2-stat"><strong>92%</strong><span>Betalda</span></div>
      <div className="s2-stat"><strong>228</strong><span>Fakturor</span></div>
      <div className="s2-stat"><strong>20</strong><span>Obetalda</span></div>
    </div>
    <div className="s2-progress"><i style={{ width: "92%" }} /></div>
  </div>;

// A single partner logo on a tinted panel, used as the visual next to an integration description.
export const LogoPanel = (props: { img: string, alt: string, tint: "blue" | "orange" | "green" }) =>
  <div className={"s2-logo-panel s2-tint-" + props.tint}>
    <div className="s2-logo-panel-inner"><img src={props.img} alt={props.alt} /></div>
  </div>;
